/**
 * OnboardingPage
 *
 * Shown to first-time users before they reach the main app.
 * They pick their preferred display currency and click "Başla".
 * On confirm → POST /api/onboarding/complete → Clerk publicMetadata.onboardingComplete = true
 * → user.reload() to refresh the JWT → navigate to /dashboard
 */
import { useState } from 'react';
import { useLingui } from '@lingui/react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import { useApiClient } from '../api/client';
import { SUPPORTED_DISPLAY_CURRENCIES, getCurrencyName } from '@wealix/shared';
import CurrencyFlag from '../components/CurrencyFlag';

export default function OnboardingPage() {
  const { _ } = useLingui();
  const { user } = useUser();
  const api = useApiClient();
  const navigate = useNavigate();

  const CURRENCY_LABELS: Record<string, string> = {
    PLN: _({ id: 'onboarding.currency.pln' }),
    USD: _({ id: 'onboarding.currency.usd' }),
    EUR: _({ id: 'onboarding.currency.eur' }),
    GBP: _({ id: 'onboarding.currency.gbp' }),
  };

  function getCurrencyLabel(code: string): string {
    return CURRENCY_LABELS[code] ?? getCurrencyName(code) ?? code;
  }

  const [selected, setSelected] = useState<string>('USD');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleComplete() {
    setLoading(true);
    setError(null);
    try {
      await api.completeOnboarding(selected);
      // Reload the Clerk user so publicMetadata.onboardingComplete is immediately visible
      await user?.reload();
      void navigate('/dashboard', { replace: true });
    } catch {
      setError(_({ id: 'onboarding.error.saveFailed' }));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-base-200 flex items-center justify-center p-4">
      <div className="card bg-base-100 shadow-xl w-full max-w-md">
        <div className="card-body gap-6">
          {/* Logo / branding */}
          <div className="text-center space-y-1">
            <div className="text-4xl">📊</div>
            <h1 className="text-2xl font-bold">
              {_({ id: 'onboarding.title', message: "Portfolyo AI'ye Hoş Geldin" })}
            </h1>
            <p className="text-base-content/60 text-sm">
              {_({ id: 'onboarding.subtitle.line1' })}
              <br />
              {_({ id: 'onboarding.subtitle.line2' })}
            </p>
          </div>

          {/* Currency selector */}
          <div className="space-y-3">
            <label className="label">
              <span className="label-text font-medium">
                {_({ id: 'onboarding.currencySelector.label' })}
              </span>
            </label>
            <div className="grid grid-cols-2 gap-3">
              {SUPPORTED_DISPLAY_CURRENCIES.map((code) => (
                <button
                  key={code}
                  type="button"
                  onClick={() => setSelected(code)}
                  className={`btn btn-outline h-auto py-3 flex-col gap-1 ${
                    selected === code ? 'btn-primary' : ''
                  }`}
                >
                  <CurrencyFlag currency={code} className="w-8 h-5 rounded-sm shadow-sm" />
                  <span className="text-lg font-bold">{code}</span>
                  <span className="text-xs font-normal opacity-70">{getCurrencyLabel(code)}</span>
                </button>
              ))}
            </div>
          </div>

          {error && (
            <div className="alert alert-error">
              <span>{error}</span>
            </div>
          )}

          {/* Submit */}
          <button
            type="button"
            className="btn btn-primary w-full"
            disabled={loading}
            onClick={() => void handleComplete()}
          >
            {loading ? <span className="loading loading-spinner loading-sm" /> : null}
            {loading ? _({ id: 'onboarding.action.saving' }) : _({ id: 'onboarding.action.start' })}
          </button>
        </div>
      </div>
    </div>
  );
}
